import {
  Box,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Flex,
  Select,
  Button,
} from '@chakra-ui/react';
import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/store';
import { getMirrorStatistics, Mirror } from '../../../api/AdminAPI';

const MirrorStatisticsTable = () => {
  const [mirrors, setMirrors] = useState<Mirror[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [limit, setLimit] = useState(20);
  const [offset, setOffset] = useState(0);
  const [total, setTotal] = useState(0);
  const [orderBy, setOrderBy] = useState('total_users');

  const token = useSelector((state: RootState) => state.userData.apiKey);

  const fetchData = async () => {
    if (!token) return;

    setIsLoading(true);
    setError(null);

    const response = await getMirrorStatistics(token, limit, offset, orderBy);

    if (response.success && response.data) {
      setMirrors(response.data.mirrors);
      setTotal(response.data.pagination.total);
    } else {
      setError(response.error || 'Failed to load data');
    }

    setIsLoading(false);
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, [token, limit, offset, orderBy]);

  const getStatusBadge = (status: number) => {
    switch (status) {
      case 0:
        return <Badge colorScheme="gray">Inactive</Badge>;
      case 2:
        return <Badge colorScheme="green">Active</Badge>;
      default:
        return <Badge colorScheme="yellow">Unknown</Badge>;
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  if (error) {
    return (
      <Box p={4} borderRadius="md" bg="status.errorSubtle" color="status.error">
        <Text>Error loading data: {error}</Text>
      </Box>
    );
  }

  return (
    <Box
      className="chart-container"
      p={6}
      bg="bg.surface"
      borderRadius="lg"
      boxShadow="sm"
      border="1px solid"
      borderColor="border.subtle"
    >
      <Flex justify="space-between" align="center" mb={4} flexWrap="wrap" gap={4}>
        <Box>
          <Text fontSize="xl" fontWeight="bold">
            🤖 Mirror Bots Statistics
          </Text>
          <Text fontSize="sm" color="text.secondary">
            Total bots: {total}
          </Text>
        </Box>

        <Flex gap={2} align="center">
          <Text fontSize="sm">Sort by:</Text>
          <Select
            value={orderBy}
            onChange={(e) => setOrderBy(e.target.value)}
            width="180px"
            size="sm"
          >
            <option value="total_users">By users</option>
            <option value="daily_active_users">By daily activity</option>
            <option value="monthly_active_users">By monthly activity</option>
            <option value="created_at">By creation date</option>
          </Select>

          <Select
            value={limit}
            onChange={(e) => {
              setLimit(Number(e.target.value));
              setOffset(0);
            }}
            width="120px"
            size="sm"
          >
            <option value={10}>10 bots</option>
            <option value={20}>20 bots</option>
            <option value={50}>50 bots</option>
            <option value={100}>100 bots</option>
          </Select>
        </Flex>
      </Flex>

      {isLoading ? (
        <Flex justify="center" align="center" height="200px">
          <Text color="text.secondary">Loading data...</Text>
        </Flex>
      ) : (
        <>
          <Box overflowX="auto">
            <Table variant="simple" size="sm">
              <Thead>
                <Tr>
                  <Th>Bot</Th>
                  <Th>Status</Th>
                  <Th isNumeric>Total</Th>
                  <Th isNumeric>Today</Th>
                  <Th isNumeric>Day</Th>
                  <Th isNumeric>Week</Th>
                  <Th isNumeric>Month</Th>
                  <Th>Created</Th>
                  <Th>Last Activity</Th>
                </Tr>
              </Thead>
              <Tbody>
                {mirrors.map((mirror) => (
                  <Tr key={mirror.mirror_id}>
                    <Td>
                      <Text fontWeight="semibold" fontSize="sm">
                        @{mirror.bot_username}
                      </Text>
                      <Text fontSize="xs" color="text.secondary">
                        ID: {mirror.bot_id}
                      </Text>
                    </Td>
                    <Td>{getStatusBadge(mirror.status)}</Td>
                    <Td isNumeric fontWeight="bold">
                      {mirror.total_users.toLocaleString()}
                    </Td>
                    <Td isNumeric>
                      <Badge colorScheme={mirror.today_active_users > 0 ? 'green' : 'gray'}>
                        {mirror.today_active_users}
                      </Badge>
                    </Td>
                    <Td isNumeric>{mirror.daily_active_users}</Td>
                    <Td isNumeric>{mirror.weekly_active_users}</Td>
                    <Td isNumeric>{mirror.monthly_active_users}</Td>
                    <Td fontSize="xs">{formatDate(mirror.created_at)}</Td>
                    <Td fontSize="xs">{formatDate(mirror.last_user_activity)}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>

          {/* Pagination */}
          <Flex justify="space-between" align="center" mt={4}>
            <Text fontSize="sm" color="text.secondary">
              Showing {offset + 1}-{Math.min(offset + limit, total)} of {total}
            </Text>
            <Flex gap={2}>
              <Button
                size="sm"
                onClick={() => setOffset(Math.max(0, offset - limit))}
                isDisabled={offset === 0}
              >
                Back
              </Button>
              <Button
                size="sm"
                onClick={() => setOffset(offset + limit)}
                isDisabled={offset + limit >= total}
              >
                Forward
              </Button>
            </Flex>
          </Flex>
        </>
      )}
    </Box>
  );
};

export default MirrorStatisticsTable;

